// Rejected goods step 1 (2026-10-24; approved mockup AUMPidbNn2tJqJ4ujXtcPv, "The receipt page" screens 3–4):
// on a receipt saved by the new window, each rejected line says what happens next and how far it got. No "Return (n)"
// button there (D1): the return was opened at the door, a replacement and a discount have their own path, a write-off
// waits for Ahmed. A step that failed when the receipt was saved is shown with a button to do it now; a write-off
// Ahmed did not approve gets "Choose another outcome". The server decides every state (receiving.service receiptCases).
// Step 4a (2026-10-25, mockup DyrAbPJDgtF8t6mjFfytkv "Door & returns" 2): a short close says who closed it and when, and what the vendor
// owes us when it was paid before; later returns of the accepted goods are listed by the "Return goods" button (GRNView).
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, DatePicker, Input, Modal, Select, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { chooseAnotherOutcome, ReceiptCase } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { MR_ACTION_OPTIONS, money, OUTCOME_OPTIONS, OutcomeCard } from './shared';

const MUTED = '#8a94a6';
const d = (s: string | null | undefined) => (s ? dayjs(s).format('D MMM') : '');
const qtyOf = (c: ReceiptCase) => `${c.qty.toLocaleString('en-US', { maximumFractionDigits: 2 })}${c.unit ? ` ${c.unit}` : ''}`;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const errText = (e: any, fallback: string) => e?.response?.data?.error || fallback;
// Step 2 (2026-10-25): how a return went back and how a case was settled, in the words of the Returns & credits page.
const SHIP_WORDS: Record<string, string> = { in_person: 'in person', courier: 'by courier', collected: 'the vendor collected it' };
const REFUND_WORDS: Record<string, string> = { cash: 'cash', instapay: 'InstaPay', transfer: 'transfer into ADIB' };
function settledText(st: NonNullable<ReceiptCase['settlement']>) {
  const on = st.on ? `, ${d(st.on)}` : '';
  if (st.how === 'refund') return `Refunded${st.refundMethod ? ` · ${REFUND_WORDS[st.refundMethod] || st.refundMethod}` : ''}${st.reference ? ` · ${st.reference}` : ''}${on}${st.amount ? ` · EGP ${money(st.amount)}` : ''}`;
  if (st.how === 'other_invoice') return `Taken off another invoice${on}${st.amount ? ` · EGP ${money(st.amount)}` : ''}`;
  if (st.how === 'replaced') return `Replaced instead${st.replacedBy ? ` · ${st.replacedBy}` : ''}${on}`;
  if (st.how === 'paid_less') return `Paid EGP ${money(st.amount || 0)} less${on}`;
  return `Credited${st.reference ? ` · ${st.reference}` : ''}${on}`;
}

/** The banner over the lines (mockup receipt 3): "4 rejected lines in this delivery. 1 returned to Salem (shipped) · …". */
export function caseSummary(cases: ReceiptCase[], vendor: string | null): { title: string; parts: string[]; open: boolean } {
  const v = vendor || 'the vendor';
  const count = (pred: (c: ReceiptCase) => boolean) => cases.filter(pred).length;
  const parts: string[] = [];
  const add = (n: number, text: (n: number) => string) => { if (n) parts.push(text(n)); };
  // Mockup "Door & returns" 2: short closes first, then replacements, then discounts.
  add(count((c) => c.outcome === 'short_close' && c.state === 'not_closed'), (n) => `${plural(n, 'short close')} not done yet`);
  add(count((c) => c.outcome === 'short_close' && c.state === 'closed_at_door'), (n) => `${n} short-closed`);
  const shortOwed = cases.filter((c) => c.outcome === 'short_close' && (c.owed || 0) > 0);
  if (shortOwed.length) parts.push(`${v} owes EGP ${money(shortOwed.reduce((s, c) => s + (c.owed || 0), 0), 0)} for short-closed goods paid before`);
  add(count((c) => c.outcome === 'return' && c.state === 'not_created'), (n) => `${plural(n, 'return')} not created yet`);
  add(count((c) => c.outcome === 'return' && c.state === 'to_ship'), (n) => `${n} to return to ${v} (to ship)`);
  add(count((c) => c.outcome === 'return' && c.state === 'waiting'), (n) => `${n} returned to ${v} (shipped)`);
  add(count((c) => c.outcome === 'return' && (c.state === 'credited' || c.state === 'refunded' || c.state === 'replaced')), (n) => `${plural(n, 'return')} settled`);
  const due = cases.filter((c) => c.outcome === 'replacement' && (c.state === 'due' || c.state === 'late'));
  if (due.length) {
    const first = due.map((c) => c.due || '').filter(Boolean).sort()[0];
    parts.push(`${plural(due.length, 'replacement')} ${due.some((c) => c.state === 'late') ? 'late, was due' : 'due'} ${d(first)}`);
  }
  add(count((c) => c.outcome === 'replacement' && c.state === 'received'), (n) => `${plural(n, 'replacement')} received`);
  add(count((c) => c.outcome === 'replacement' && c.state === 'closed'), (n) => `${plural(n, 'replacement')} closed without arriving`);
  // A discount: paid already → the vendor owes it back; not paid yet → we pay it less (Ahmed, 2026-10-24).
  const owed = cases.filter((c) => c.outcome === 'discount' && c.state !== 'pay_less' && c.state !== 'settled');
  if (owed.length) parts.push(`${v} owes EGP ${money(owed.reduce((s, c) => s + (c.owed || 0), 0), 0)} for ${owed.length === 1 ? 'a discount' : `${owed.length} discounts`}`);
  const less = cases.filter((c) => c.state === 'pay_less');
  if (less.length) parts.push(`pay ${v} EGP ${money(less.reduce((s, c) => s + (c.owed || 0), 0), 0)} less for ${less.length === 1 ? 'a discount' : `${less.length} discounts`}`);
  add(count((c) => c.outcome === 'discount' && c.state === 'settled'), (n) => `${plural(n, 'discount')} settled`);
  add(count((c) => c.outcome === 'writeoff' && c.state === 'not_sent'), (n) => `${plural(n, 'write-off')} not sent to Ahmed yet`);
  add(count((c) => c.outcome === 'writeoff' && c.state === 'waiting_ahmed'), (n) => `${plural(n, 'write-off')} waiting for Ahmed`);
  add(count((c) => c.outcome === 'writeoff' && c.state === 'approved'), (n) => `${plural(n, 'write-off')} approved`);
  add(count((c) => c.outcome === 'writeoff' && c.state === 'not_approved'), (n) => `${plural(n, 'write-off')} not approved: choose another outcome`);
  add(count((c) => c.closeRestNotSent), (n) => `${plural(n, 'request')} to close the rest not sent yet`);
  const rejected = count((c) => !!c.outcome);
  const open = cases.some((c) => (c.outcome || c.state || c.closeRestNotSent)
    && (!['credited', 'refunded', 'replaced', 'received', 'approved', 'settled', 'closed_at_door', null, ''].includes(c.state || '') || c.closeRestNotSent || (c.outcome === 'short_close' && (c.owed || 0) > 0)));
  return { title: rejected ? `${plural(rejected, 'rejected line')} in this delivery.` : 'A step was not done when this receipt was saved.', parts, open };
}

const Case: React.FC<{ title: React.ReactNode; fail?: boolean; children?: React.ReactNode }> = ({ title, fail, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12.5, minWidth: 0, ...(fail ? { borderLeft: '3px solid #cf1322', paddingLeft: 8 } : {}) }}>
    <b style={{ fontWeight: 600 }}>{title}</b>
    {children}
  </div>
);
const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => <span style={{ color: MUTED }}>{children}</span>;
const Act: React.FC<{ children: React.ReactNode }> = ({ children }) => <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>{children}</div>;

const retCode = (code: string | null) => (code ? <Link to={`/returns?search=${encodeURIComponent(code)}`}>{code}</Link> : null);

/** "What happens next" for one line (mockup receipt 3–4). */
export const CaseCell: React.FC<{
  c: ReceiptCase; vendor: string | null; canManage: boolean;
  onRetry: (c: ReceiptCase, step: 'return' | 'request' | 'short_close') => Promise<void>;
  onChooseAnother: (c: ReceiptCase) => void;
}> = ({ c, vendor, canManage, onRetry, onChooseAnother }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const v = vendor || 'the vendor';
  const run = async (step: 'return' | 'request' | 'short_close') => { setBusy(step); try { await onRetry(c, step); } finally { setBusy(null); } };
  const blocks: React.ReactNode[] = [];

  if (c.outcome === 'return') {
    const r = c.returns[0];
    if (c.state === 'not_created') {
      blocks.push(
        <Case key="ret" fail title="Return to vendor · not created yet">
          <span>The return was not created when the receipt was saved.</span>
          {canManage && <Act><Button size="small" type="primary" loading={busy === 'return'} onClick={() => run('return')}>Create the return now</Button></Act>}
        </Case>,
      );
    } else {
      const title = <>Return to vendor · {retCode(r?.code || c.ref)}</>;
      const shipped = [r?.shippedOn ? `Shipped ${d(r.shippedOn)}` : 'Shipped', r?.shipMethod ? SHIP_WORDS[r.shipMethod] || r.shipMethod : null,
        r?.shipMethod === 'courier' && r?.carrierName ? `${r.carrierName}${r.trackingNumber ? ` ${r.trackingNumber}` : ''}` : null].filter(Boolean).join(', ');
      const line = c.state === 'to_ship' ? 'To ship'
        : c.state === 'waiting' ? `${shipped} · waiting for ${v}`
          : c.settlement ? settledText(c.settlement)
            : c.state === 'refunded' ? `Refunded${r?.refundRef ? ` · ${r.refundRef}` : ''}${r?.refundAmount ? ` · EGP ${money(r.refundAmount)}` : ''}`
              : `Credited${r?.creditNoteRef ? ` · ${r.creditNoteRef}` : ''}`;
      blocks.push(
        <Case key="ret" title={title}>
          <span>{line}</span>
          {c.owed ? <Muted>EGP {money(c.owed)} owed</Muted> : null}
        </Case>,
      );
    }
  } else if (c.outcome === 'replacement') {
    const got = c.replacedBy;
    blocks.push(
      <Case key="rep" title="Vendor replaces it">
        {c.state === 'received'
          ? <span>Received on {got.map((g, i) => <React.Fragment key={g.grnId}>{i ? ', ' : ''}<Link to={`/grns/${g.grnId}`}>{g.grnId}</Link></React.Fragment>)}</span>
          : c.state === 'closed'
            ? <span>Closed on the PO without a replacement</span>
            : <span>{c.state === 'late' ? `Late: was due ${d(c.due)}` : `Due ${d(c.due)}`} · {v} takes the faulty ones back</span>}
        {c.state !== 'received' && got.length > 0 && <Muted>{got.map((g) => `${g.qty} on ${g.grnId}`).join(', ')} so far</Muted>}
      </Case>,
    );
  } else if (c.outcome === 'discount') {
    blocks.push(
      <Case key="disc" title="Kept at a discount">
        {c.state === 'settled' && c.settlement
          ? <span>{settledText(c.settlement)}</span>
          : c.state === 'pay_less'
            ? <span>Not paid yet: pay {v} EGP {money(c.owed || 0)} less</span>
            : <span>{v} owes us EGP {money(c.owed || 0)} · waiting for credit</span>}
      </Case>,
    );
  } else if (c.outcome === 'short_close') {
    if (c.state === 'not_closed') {
      blocks.push(
        <Case key="sc" fail title="Short close · not done yet">
          <span>The PO line was not closed when the receipt was saved.</span>
          {canManage && <Act><Button size="small" type="primary" loading={busy === 'short_close'} onClick={() => run('short_close')}>Close it now</Button></Act>}
        </Case>,
      );
    } else {
      const by = c.closedBy?.name ? `Short-closed by ${c.closedBy.name.split(' ')[0]}` : 'Short-closed';
      blocks.push(
        <Case key="sc" title={<>{by}{c.closedBy?.at ? ` · ${d(c.closedBy.at)}` : ''}</>}>
          <span>{c.note || `Closed on the PO line; nothing more is expected from ${v}`}</span>
          {(c.owed || 0) > 0
            ? <Muted>{v} owes us EGP {money(c.owed || 0)}: they were paid for</Muted>
            : c.settlement ? <Muted>{settledText(c.settlement)}</Muted> : null}
        </Case>,
      );
    }
  } else if (c.outcome === 'writeoff') {
    if (c.state === 'not_sent') {
      blocks.push(
        <Case key="wo" fail title="Write off · not sent to Ahmed yet">
          <span>The request did not reach Ahmed's Cancellations.</span>
          {canManage && <Act><Button size="small" type="primary" loading={busy === 'request'} onClick={() => run('request')}>Send it now</Button></Act>}
        </Case>,
      );
    } else if (c.state === 'not_approved') {
      blocks.push(
        <Case key="wo" title="Write off · not approved by Ahmed">
          {c.decision?.note && <Muted>“{c.decision.note}”{c.decision.by ? ` (${c.decision.by})` : ''}</Muted>}
          {c.decision?.at && <Muted>{d(c.decision.at)}{!c.decision.note && c.decision.by ? ` · ${c.decision.by}` : ''}</Muted>}
          {canManage && <Act><Button size="small" type="primary" onClick={() => onChooseAnother(c)}>Choose another outcome</Button></Act>}
        </Case>,
      );
    } else {
      blocks.push(
        <Case key="wo" title={c.state === 'approved' ? 'Write off · approved by Ahmed' : 'Write off · waiting for Ahmed'}>
          <Muted>{c.state === 'approved' ? `${d(c.decision?.at)}${c.decision?.by ? ` · ${c.decision.by}` : ''}` : 'In Cancellations'}</Muted>
        </Case>,
      );
    }
  }
  if (c.closeRestNotSent) {
    blocks.push(
      <Case key="rest" fail title="Close the rest · not sent to Ahmed yet">
        <span>The request did not reach Ahmed's Cancellations.</span>
        {canManage && <Act><Button size="small" type="primary" loading={busy === 'request'} onClick={() => run('request')}>Send it now</Button></Act>}
      </Case>,
    );
  }
  // An outcome chosen again after a write-off was not approved keeps what came before.
  const seen = new Map<string, number>();
  c.history.forEach((h) => {
    const t = `${h.outcome === 'writeoff' ? 'write off' : h.outcome}${h.decided ? `, ${h.decided}` : ''}${h.decidedAt ? ` ${d(h.decidedAt)}` : ''}`;
    seen.set(t, (seen.get(t) || 0) + 1);
  });
  const was = Array.from(seen.entries()).map(([t, n]) => (n > 1 ? `${t} (${n} times)` : t));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      {blocks}
      {was.length > 0 && <div style={{ fontSize: 11.5, color: MUTED }}>Before: {was.join(' · ')}</div>}
    </div>
  );
};

/** "Choose another outcome" after Ahmed did not approve a write-off saved before step 4a: the vendor replaces it, or short close
 *  (closed on the PO line now). Returns and write-offs are no longer chosen for rejected goods (Ahmed, 25 Oct). */
export const ChooseOutcomeWindow: React.FC<{
  c: ReceiptCase | null; item: string; vendor: string | null; onClose: () => void; onSaved: () => void;
}> = ({ c, item, vendor, onClose, onSaved }) => {
  const compact = useCompactLayout();
  const [type, setType] = useState<'replacement' | 'short_close' | null>(null);
  const [due, setDue] = useState<Dayjs | null>(null);
  const [mrAction, setMrAction] = useState<'not_needed' | 'reorder'>('not_needed');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const reset = () => { setType(null); setDue(null); setNote(''); setMrAction('not_needed'); };
  const close = () => { reset(); onClose(); };
  const ok = !!type && (type !== 'replacement' || !!due);
  const save = async () => {
    if (!c || !type) return;
    setSaving(true);
    try {
      const res = await chooseAnotherOutcome(c.grnLineId, {
        type, dueDate: due ? due.format('YYYY-MM-DD') : undefined, note: note.trim() || undefined, mrAction: type === 'short_close' ? mrAction : undefined,
      });
      (res.warnings || []).forEach((w) => message.warning(w, 8));
      message.success(type === 'replacement' ? 'Replacement recorded' : 'Short-closed on the PO line');
      reset();
      onSaved();
    } catch (e: any) {
      message.error(errText(e, 'Could not save the outcome'));
    } finally {
      setSaving(false);
    }
  };
  const text: React.CSSProperties = { fontSize: 12.5, color: '#5f6672' };
  const options = OUTCOME_OPTIONS.filter((o) => o.value === 'replacement' || o.value === 'short_close');
  return (
    <Modal
      open={!!c}
      title={c ? <div style={{ paddingInlineEnd: 28 }}>Line {c.lineNo} · {qtyOf(c)} rejected · <bdi>{item}</bdi> · <bdi>{vendor || ''}</bdi></div> : null}
      onCancel={close}
      onOk={save}
      okText="Save"
      okButtonProps={{ disabled: !ok, loading: saving }}
      maskClosable={false}
      width={compact ? 520 : 640}
    >
      {c && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={text}>
            Ahmed did not approve the write-off{c.decision?.at ? ` on ${d(c.decision.at)}` : ''}{c.decision?.note ? `: “${c.decision.note}”` : ''}. The {qtyOf(c)} are still open on the PO line; choose what happens to them now.
          </div>
          <div>
            <div id="co-next" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}><span style={{ color: '#cf1322' }}>*</span> What happens next</div>
            <div role="radiogroup" aria-labelledby="co-next" style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              {options.map((o) => (
                <OutcomeCard key={o.value} label={o.label} on={type === o.value} onPick={() => setType(o.value as typeof type)}>
                  <div style={text}>
                    {o.hint}
                    {o.value === 'replacement' && type === 'replacement' && (
                      <> Due <DatePicker size="small" value={due} onChange={setDue} format="D MMM YYYY" disabledDate={(v) => v.isBefore(dayjs(), 'day')} style={{ width: 130 }} inputReadOnly={compact} aria-label="Replacement due" />.</>
                    )}
                  </div>
                  {o.value === 'short_close' && type === 'short_close' && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select size="small" value={mrAction} onChange={setMrAction} options={MR_ACTION_OPTIONS} style={{ width: '100%' }} popupMatchSelectWidth={false} aria-label="What the requester gets" />
                    </div>
                  )}
                </OutcomeCard>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="co-note" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Note</label>
            <Input id="co-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" maxLength={300} />
          </div>
        </div>
      )}
    </Modal>
  );
};
